import React, { useState } from 'react';
import { css } from '@emotion/react';
import { Box, Stack, Text, Draft } from '@resi-media/resi-ui';
import { useLocalStorage } from 'usehooks-ts';
import { useMockData } from '@preview/contexts/MockDataContext';
import { useRefresh } from '@preview/contexts/RefreshContext';
import type { BoxCast } from '@preview/types/boxcast';

const PAGE_SIZE = 50;

const chunkArray = <T,>(arr: T[], count: number): T[][] => {
  const size = Math.ceil(arr.length / count);
  return Array.from({ length: count }, (_, i) => arr.slice(i * size, (i + 1) * size)).filter(
    (chunk) => chunk.length > 0
  );
};

const BoxCastDataFetcherCard: React.FC = () => {
  const [channelId, setChannelId] = useLocalStorage('preview-boxcast-channel-id', '');
  const [maxBroadcasts, setMaxBroadcasts] = useLocalStorage('preview-boxcast-max-broadcasts', 20);
  const [playlistCount, setPlaylistCount] = useLocalStorage('preview-boxcast-playlist-count', 1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { addPlaylistMock, addSiteMock, addVideoMock, clearAllMocks, embedId, resetPendingChanges } = useMockData();
  const { triggerRefresh } = useRefresh();

  const fetchAllBroadcasts = async (): Promise<BoxCast.Broadcast[]> => {
    const broadcasts: BoxCast.Broadcast[] = [];
    let page = 0;

    while (broadcasts.length < maxBroadcasts) {
      const response = await fetch(
        `https://api.boxcast.com/channels/${channelId}/broadcasts?l=${PAGE_SIZE}&p=${page}&s=-starts_at`
      );
      if (!response.ok) throw new Error(`BoxCast API error: ${response.status}`);

      const items: BoxCast.BroadcastsResponse = await response.json();

      const past = items.filter((b) => b.timeframe === 'past' && !b.is_private);
      broadcasts.push(...past);

      if (items.length < PAGE_SIZE) break;
      page++;
    }

    return broadcasts.slice(0, maxBroadcasts);
  };

  const fetchBoxCastData = async () => {
    if (!channelId.trim()) {
      setError('Channel ID is required');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const broadcasts = await fetchAllBroadcasts();

      if (broadcasts.length === 0) {
        throw new Error('No past broadcasts found for this channel');
      }

      clearAllMocks();

      const siteId = `site-boxcast-${channelId}`;
      const basePlaylistId = `boxcast-${channelId}`;

      const videos = broadcasts.map((broadcast) => ({
        id: broadcast.id,
        title: broadcast.name,
        description: broadcast.description,
        thumbnail: { url: broadcast.poster || broadcast.preview },
        airDate: broadcast.starts_at,
        embedId,
        formattedDescription: `<p>${broadcast.description}</p>`,
      }));

      const chunks = chunkArray(videos, Math.min(playlistCount, videos.length));

      addSiteMock(
        {
          id: siteId,
          description: `BoxCast Channel: ${channelId}`,
          endpoint: '*sites-boot.resi.io/api/v1/sites?url=*',
          data: {
            id: siteId,
            customerId: `customer-boxcast-${channelId}`,
            channelId: 'preview-channel-id',
            name: 'BoxCast Channel',
            description: 'BoxCast Channel Content',
            url: `https://boxcast.tv/channel/${channelId}`,
            branding: { mode: 'dark' },
            feeds: [
              {
                id: `feed-boxcast-${channelId}`,
                name: 'BoxCast Feed',
                description: 'Content from BoxCast channel',
                default: true,
                playlists: chunks.map((chunk, i) => ({
                  id: `${basePlaylistId}-${i + 1}`,
                  name: chunks.length > 1 ? `Broadcasts ${i + 1}` : 'All Broadcasts',
                  size: chunk.length,
                  url: `/api/playlists/${basePlaylistId}-${i + 1}`,
                })),
              },
            ],
          },
        },
        true
      );

      chunks.forEach((chunk, i) => {
        const playlistId = `${basePlaylistId}-${i + 1}`;
        const playlistName = chunks.length > 1 ? `Broadcasts ${i + 1}` : 'All Broadcasts';

        addPlaylistMock(
          {
            id: playlistId,
            endpoint: `*/api/playlists/${playlistId}*`,
            description: `BoxCast Channel: ${channelId} (${i + 1}/${chunks.length})`,
            data: { id: playlistId, name: playlistName, videos: chunk },
          },
          true
        );

        chunk.forEach((video) => {
          addVideoMock(
            {
              id: video.id,
              description: `BoxCast Broadcast: ${video.title}`,
              endpoint: `*/ondemand/videos/${video.id}`,
              data: {
                id: video.id,
                title: video.title,
                description: video.description,
                thumbnail: { url: video.thumbnail.url },
                airDate: video.airDate,
                embedId,
                tags: ['boxcast'],
                playlists: [{ id: playlistId, url: `*/api/playlists/${playlistId}*` }],
              },
            },
            true
          );
        });
      });

      setSuccess(`BoxCast data fetched: ${broadcasts.length} broadcasts across ${chunks.length} playlist(s).`);
      triggerRefresh();
      resetPendingChanges();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Draft.Card>
      <Draft.CardSection>
        <Stack gap="m">
          <Text variant="h3">BoxCast Data Fetcher</Text>
          <Text>Fetch broadcasts from a BoxCast channel and transform them into mock data</Text>

          <Stack gap="s">
            <Draft.FormField fieldLabel="Channel ID">
              <Draft.TextInput
                css={css`
                  width: 100%;
                `}
                onChange={(e) => setChannelId(e.target.value)}
                placeholder="Enter BoxCast channel ID"
                value={channelId}
              />
            </Draft.FormField>
            <Text colorVariant="secondary" variant="body3">
              Find the channel ID at boxcast.tv/channel/{'<channelId>'} or in the BoxCast dashboard.
            </Text>

            <Box
              css={css`
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 16px;
              `}
            >
              <Draft.FormField fieldLabel="Max Broadcasts">
                <Draft.TextInput
                  css={css`
                    width: 100%;
                  `}
                  max={200}
                  min={1}
                  onChange={(e) => setMaxBroadcasts(parseInt(e.target.value, 10) || 20)}
                  type="number"
                  value={maxBroadcasts.toString()}
                />
              </Draft.FormField>
              <Draft.FormField fieldLabel="Playlist Count">
                <Draft.TextInput
                  css={css`
                    width: 100%;
                  `}
                  max={50}
                  min={1}
                  onChange={(e) => setPlaylistCount(parseInt(e.target.value, 10) || 1)}
                  type="number"
                  value={playlistCount.toString()}
                />
              </Draft.FormField>
            </Box>

            {error && <Text color="error">{error}</Text>}
            {success && <Text color="success">{success}</Text>}

            <Draft.Button
              css={css`
                margin-top: 8px;
              `}
              disabled={isLoading}
              label="Fetch BoxCast Data"
              onClick={fetchBoxCastData}
            >
              {isLoading ? 'Loading...' : 'Fetch BoxCast Data'}
            </Draft.Button>
          </Stack>
        </Stack>
      </Draft.CardSection>
    </Draft.Card>
  );
};

export default BoxCastDataFetcherCard;
